<template>
  <div>
    <v-card class="mt-0 elevation-0 rounded-0 borderless">
      <v-card-title class="mt-n1 mr-2">
        <button-tip btnclass="ml-n1 mt-n1 mr-2" @click="refresh = true, getPeople()" elevation="5" fab icolor="black"
                    iname="mdi-update" tip="Refresh People" xsmall/>
        <span style="font-weight: normal; text-align: right; width: 50px">{{people.length || ''}}</span>&nbspPeople
        <information-dialog-icon confirmClass="mr-3 title" confirmColor="secondary darken-1"
                                 tclass="headline grey lighten-2 mb-8" title="People Information">
          <p>In the <strong>first</strong> field, type all or part of a name or phone number, then press return to
            search the database for a person. A limit of {{limit}} will be returned.
            (The number returned will be displayed, but not changed by the "Filter results" field.)</p>
          <p>In the <strong>second</strong> field, optionally type a search string to filter results.</p>
          <p>After search results are returned, <strong>sort</strong> can be switched from the default "Name" column,
            to "Surname, Given" (undisplayed columns) by clicking the switch to "Surname Sort".</p>
        </information-dialog-icon>
        <v-text-field class="smalltext mr-4"
                      clearable
                      dense
                      hint="Search database by name/phone"
                      @keyup.enter="!!searchPeopleInDatabase ? getPeople() : ''"
                      persistent-hint
                      single-line
                      style="width:calc(160px + 7vw)"
                      v-model="searchPeopleInDatabase"/>
        <v-switch color="secondary"
                  dense
                  :disabled="people && !people.length"
                  label="Surname Sort"
                  v-model="sortSwitchedToSurname"
                  x-small/>
        <v-spacer/>
        <v-row>
          <v-text-field append-icon="mdi-magnify"
                        class="mx-2"
                        clearable
                        dense
                        hint="Filter results"
                        persistent-hint
                        single-line
                        style="width:9vw"
                        v-model="search"/>
          <button-tip @click="createNewPerson()" color="secondary" elevation="5" fab icolor="white"
                      iname="mdi-plus" tip="Create Person" v-can="'people-edit'" xsmall/>
        </v-row>
      </v-card-title>
      <v-data-table
          class="px-0 mx-1 mt-n2 mb-3 pb-1 elevation-2 med-table-rows pad-last-table-row peopleTable"
          @click:row="rowClick"
          fixed-header
          :headers="colHeaders"
          hide-default-footer
          item-key="plid"
          :items-per-page="-1"
          :items="people"
          :loading="loadingPeople"
          loading-text="Loading... Please wait"
          :mobile-breakpoint="0"
          multi-sort
          :search="search"
          single-select
          :sort-by="sortSelected"
          :style="$_cssVars">
        <template v-slot:item.actions="{ item }">
          <v-lazy>
            <div>
              <button-tip @click="$_editPerson(item)" icon iname="mdi-open-in-new" tip="Person Popup" xsmall/>
              <button-tip @click="confirmDeletePerson(item)" icolor="red lighten-2" icon iname="mdi-trash-can-outline"
                          tip="Delete Person" v-if="$_canAny(['member-override','people-override'])" xsmall/>
            </div>
          </v-lazy>
        </template>
        <template v-for="field in fields" v-slot:[`item.${field}`]="{ item }">
          <span v-if="item[field]">
            <div v-if="(phones.includes(field))">{{ item[field].replace(/(\d{3})(\d{3})(\d{4})/,'($1) $2-$3') }}</div>

            <v-tooltip left v-else-if="field === 'name' && item[field].length > 41 && (item.suffix || item.salutation)">
              <template v-slot:activator="{ on }"><!-- don't slice off of htmlName because it has embedded tags -->
                <div v-on="on" v-html="item.htmlName + item.postname.slice(0, Math.max(0, 41 - item.htmlName.length)) + '...'"/>
              </template>
              <div v-html="item.htmlName + item.postname"/>
            </v-tooltip>

            <v-tooltip left v-else-if="field === 'email' && item[field].length > 41">
              <template v-slot:activator="{ on }">
                <div v-on="on">{{ item[field].slice(0, 36) + '...' }}</div>
              </template>
              {{ item[field] || '' }}
            </v-tooltip>

            <!-- firms_sort clickable + tooltip if > 41 -->
            <template v-else-if="field === 'firms_sort' && item[field].length > 41">
              <v-tooltip left>
                <template v-slot:activator="{ on }">
                  <a class="linkish-trigger" @click="goToFirmMembersTabInPopup(item)" v-on="on">
                    {{ item[field].slice(0, 36) + '...' }}
                  </a>
                </template>
                {{ item[field] }}
              </v-tooltip>
            </template>

            <!-- firms_sort clickable if short -->
            <template v-else-if="field === 'firms_sort'">
              <a class="linkish-trigger" @click="goToFirmMembersTabInPopup(item)">
                {{ item[field] }}
              </a>
            </template>

            <!-- contacts_sort clickable + tooltip if > 41 -->
            <template v-else-if="field === 'contacts_sort' && item[field].length > 41">
<!--            <template v-else-if="field === 'cid' && item[field].length > 41">--><!-- for testing -->
              <v-tooltip left>
                <template v-slot:activator="{ on }">
                  <a class="linkish-trigger" @click="goToContactInPopup(item)" v-on="on">
                    {{ item[field].slice(0, 36) + '...' }}
                  </a>
                </template>
                {{ item[field] }}
              </v-tooltip>
            </template>

            <!-- contacts_sort clickable if short -->
            <template v-else-if="field === 'contacts_sort'">
<!--            <template v-else-if="field === 'cid'">--><!-- for testing -->
              <a class="linkish-trigger" @click="goToContactInPopup(item)">
                {{ item[field] }}
              </a>
            </template>

            <div v-else-if="field === 'name'" v-html="item.htmlName + item.postname"/>
            <div v-else>{{ item[field] || '' }}</div>
          </span>
        </template>

      </v-data-table>
    </v-card>

    <!--  Dialogs  -->
    <ActionDialog :value="confirmDeleteDialog" :max-width="700" divider confirm-text="Delete Person"
                  title="Delete Person" close-text="Cancel" confirm-color="error"
                  @confirm="deletePerson" @close="confirmDeleteDialog = false">
      <v-alert class="mt-6" border="bottom" colored-border type="warning" elevation="2">
        Are you sure you want to <strong>delete</strong> this person?
        <br><br>sort: {{personToDelete.sort}}<br><br>
        This deletes it from the database, with no way to recover. Click "DELETE" below to proceed with deletion.
      </v-alert>
    </ActionDialog>
    <ErrorDialog ref="errorDialog"/>
    <snack :color="$_snack.color" :msg="$_snack.msg" v-model="$_snack.open" v-if="$_snack.cmpnt==='PeopleSearch'"></snack>
  </div>
</template>

<script>
  import ActionDialog from "@/components/ActionDialog";
  import { ContactAPIService } from "@/servicehandlers/ContactAPIService";
  import EditPerson from "@/components/search/firms/EditPerson";
  import { PeopleAPIService } from "@/servicehandlers/PeopleAPIService";

  const contactAPIService = new ContactAPIService();
  const peopleAPIService = new PeopleAPIService();

  export default {
    name: 'PeopleSearch',
    components: { ActionDialog, EditPerson },
    data() {
      return {
        colHeaders: [// FIELDS FROM LEGACY: Name, Direct, Fax, Home, Mobile, Email, Firm, Contact
          { value: 'actions',      text: '',       width: "75px", sortable: false, align:'center' },
          { value: 'name',         text: 'Name',   width: "325px", sortable: true, align: 'start' },
          { value: 'direct',       text: 'Direct', width: "130px", sortable: true, align: 'start' },
          { value: 'fax',          text: 'Fax',    width: "130px", sortable: true, align: 'start' },
          { value: 'home',         text: 'Home',   width: "130px", sortable: true, align: 'start' },
          { value: 'mobile',       text: 'Mobile', width: "130px", sortable: true, align: 'start' },
          { value: 'email',        text: 'Email',  width: "270px", sortable: true, align: 'start' },
          { value: 'firms_sort',   text: 'Firm',   width: '321px', sortable: true, align: 'start' },
          { value: 'contacts_sort',text: 'Contact',width: '110px', sortable: true, align: 'start' },
        ],
        confirmDeleteDialog: false,
        limit: 1000,// overrides default of 100 in PeopleAPIService
        loadingPeople: false,
        names: ['prefix','given','surname','suffix','salutation'],
        phones: ['direct','fax','home','mobile'],
        people: [],
        personToDelete: {},
        refresh: false,
        search: '',
        searchPeopleInDatabase: '',
        sortSelected: ['name'],// default sort by name column
        sortSwitchedToSurname: false,
      };
    },

    watch: {
      sortSwitchedToSurname() {
        this.sortSelected = this.sortSwitchedToSurname ? ['surname', 'given'] : ['name'];
        this.getPeople();// refresh people table to show new sort order
      }
    },

    computed: {
      fields() {
        return this.colHeaders.map(a => a.value === 'actions' ? '' : a.value);//fields are all colHeaders except actions
      },
    },

    created() {
      document.title = 'People Search';
    },

    methods: {
      confirmDeletePerson(person) {
        this.personToDelete = person;
        this.confirmDeleteDialog = true;
      },

      createNewPerson() {
        const params = {};// no initial params needed for new person
        peopleAPIService.createPerson(params, this.$router).then((person) => {
          this.$_editPerson(person);// editPerson component is also used to edit a member
        }).catch((e) => {
          this.$refs.errorDialog.showError({
            errorMessage: "Failed to create new person",
            forDevelopers: e
          })
        });
      },

      deletePerson() {
        peopleAPIService.deletePerson(this.personToDelete.plid, this.$router).then((res) => {
          this.confirmDeleteDialog = false;
          this.getPeople();// refresh people table to show person is deleted
          this.$store.dispatch('SET_SNACK', {cmpnt: 'PeopleSearch', open: true, msg: 'Person deleted'});
        }).catch((e) => {
          this.confirmDeleteDialog = false;
          this.$refs.errorDialog.showError({
            errorMessage: "Failed to delete person",
            forDevelopers: e
          })
        });
      },

      getPeople() {
        let search = this.searchPeopleInDatabase;
        this.search = '';// clear "Filter results" field
        this.loadingPeople = true;
        peopleAPIService.searchPerson(search, this.limit, this.$router).then((data) => {
          if (data) {
            this.loadingPeople = false;
            this.people = data;
            for (let p of this.people) {// trim extra spaces, replace � with ' & format fields for improved sort/display
              for (let fld in p) if (p[fld] && this.names.includes(fld)) p[fld] = p[fld].trim().replace(/�/g, "'");
              p.suffix = p.suffix ? (p.suffix.substring(0, 2) === ', ' ? p.suffix : ', ' + p.suffix) : '';//no comma, add
              // if there's a prefix & a salutation that doesn't include the prefix, add prefix & surname to salutation
              if (p.prefix && p.salutation && !p.salutation.includes(p.prefix)) p.salutation = `${p.salutation} or ${p.prefix} ${p.surname}`;
              // if there's a prefix and no salutation, use prefix + surname as salutation
              if ((p.prefix && !p.salutation)) p.salutation = `${p.prefix} ${p.surname}`;
              if (p.salutation) p.salutation = `(${p.salutation})`;//add parenthesis if salutation
              p.name = `${p.given || ''} ${p.surname || ''}${p.suffix} ${p.salutation}`;//set name column
              p.postname = `${p.suffix ?? ''} ${p.salutation ?? ''}`;//part that could be whole/part sub w/ ellipsis
              p.htmlName = `${p.given || ''}<b> ${p.surname}</b>`;//part that can't be shortened because of bold tags
            }
          }
        }).catch((e) => {
          this.loadingPeople = false;
          this.$refs.errorDialog.showError({
            errorMessage: "Failed to get people for this search",
            forDevelopers: e
          })
        });
      },

      goToFirmMembersTabInPopup(person) {// passes fid and opens firmPopup and takes to Members tab
        this.$_viewFirmNoNotePopup(person.fid, 'Members');
      },

      goToContactInPopup(person) {// passes cid and opens contact popup
        let baseUrl = window.location.origin;
        window.open(baseUrl +  '/editContact/' +  person.cid, '_blank',
            'resizeable=yes, width=' + (1200)
            + ', left=' + (500)
            + ', top=' + (64)
            + ', height=' + (760));
      },

      rowClick: function (item, row) {// highlights selected row
        row.select(true);
      },
    }
  };
</script>

<style>
  .peopleTable .v-data-table__wrapper { height: calc(100vh - var(--offset) - 87px) !important }
</style>
